/**
 * Spec 028: distance and area units.
 *
 * Values are stored in the unit they were entered in (`mi` or `km`, `sqft` or
 * `sqm`), so converting happens only when a value is shown, or used, in another
 * unit. Rounding follows spec D5: limits to whole numbers, distances away to one
 * decimal, areas to whole numbers, prices per unit to cents. The same cases are
 * checked by `__fixtures__/locale.fixture.json` here, on the web and in the app.
 */

export type DistanceUnit = 'mi' | 'km';
export type AreaUnit = 'sqft' | 'sqm';
export type MeasurementSystem = 'imperial' | 'metric';

export const KM_PER_MILE = 1.609344;
export const METERS_PER_MILE = 1609.344;
export const SQM_PER_SQFT = 0.09290304;
/** Mean Earth radius (IUGG). */
export const EARTH_RADIUS_M = 6371008.8;
/** The same radius in miles, for the SQL distance formulas that work in miles. */
export const EARTH_RADIUS_MILES = EARTH_RADIUS_M / METERS_PER_MILE;

export const distanceUnitFor = (system: MeasurementSystem | null | undefined): DistanceUnit =>
  system === 'metric' ? 'km' : 'mi';

export const areaUnitFor = (system: MeasurementSystem | null | undefined): AreaUnit =>
  system === 'metric' ? 'sqm' : 'sqft';

/** Reads a stored unit; anything but `km` is miles (rows from before spec 028). */
export const asDistanceUnit = (value: unknown): DistanceUnit => (value === 'km' ? 'km' : 'mi');

/** Reads a stored area unit; anything but `sqm` is square feet. */
export const asAreaUnit = (value: unknown): AreaUnit => (value === 'sqm' ? 'sqm' : 'sqft');

const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON * Math.sign(value)) * factor) / factor;
};

/** A distance in another unit, not rounded. */
export const convertDistance = (value: number, from: DistanceUnit, to: DistanceUnit): number => {
  if (from === to) return value;
  return from === 'mi' ? value * KM_PER_MILE : value / KM_PER_MILE;
};

/**
 * A distance limit (per day, per month, per trip) in another unit, as a whole
 * number. -1 (unlimited) stays -1; a value in its own unit is left as it is.
 */
export const convertLimit = (value: number, from: DistanceUnit, to: DistanceUnit): number => {
  if (value === -1 || from === to) return value;
  return Math.round(convertDistance(value, from, to));
};

/** A price per mile or per km in the other unit, in cents. */
export const convertPricePerUnit = (
  amount: number,
  from: DistanceUnit,
  to: DistanceUnit,
): number => {
  if (from === to) return amount;
  // $/mi → $/km divides by 1.609; $/km → $/mi multiplies.
  return roundTo(from === 'mi' ? amount / KM_PER_MILE : amount * KM_PER_MILE, 2);
};

/** An area in another unit, as a whole number. */
export const convertArea = (value: number, from: AreaUnit, to: AreaUnit): number => {
  if (from === to) return value;
  return Math.round(from === 'sqft' ? value * SQM_PER_SQFT : value / SQM_PER_SQFT);
};

export interface LatLng {
  lat: number;
  lng: number;
}

/** Great-circle distance in metres. */
export const haversineMeters = (a: LatLng, b: LatLng): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Great-circle distance in miles (the unit search APIs answer in, spec D3). */
export const haversineMiles = (a: LatLng, b: LatLng): number =>
  haversineMeters(a, b) / METERS_PER_MILE;

const wholeNumber = (value: number): string =>
  Math.round(value).toLocaleString('en-US', { maximumFractionDigits: 0 });

const unitWord = (value: number, unit: DistanceUnit): string =>
  unit === 'km' ? 'km' : Math.abs(value) === 1 ? 'mile' : 'miles';

export type DistanceStyle = 'perDay' | 'allowance' | 'away' | 'plain';

/**
 * Distance text. Mile text is today's wording ("200 miles per day", "1,200
 * miles", "5.0 miles away"); only an unlimited allowance reads "Unlimited
 * distance" on every brand (spec Q5).
 */
export const formatDistance = (value: number, unit: DistanceUnit, style: DistanceStyle): string => {
  if (style === 'away') {
    return `${roundTo(value, 1).toFixed(1)} ${unit === 'km' ? 'km' : 'miles'} away`;
  }
  if (value === -1 && style !== 'plain') return 'Unlimited distance';
  const number = wholeNumber(value);
  if (style === 'perDay') return `${number} ${unit === 'km' ? 'km' : 'miles'} per day`;
  return `${number} ${unitWord(value, unit)}`;
};

/** "/km" or "/mile", for prices per unit. */
export const perUnitSuffix = (unit: DistanceUnit): string => (unit === 'km' ? '/km' : '/mile');

export const formatArea = (value: number, unit: AreaUnit): string =>
  `${wholeNumber(value)} ${unit === 'sqm' ? 'm²' : 'sq ft'}`;

/** Spec D6: the daily allowance a car starts with. */
export const defaultDailyDistance = (unit: DistanceUnit): number => (unit === 'km' ? 300 : 200);

/** Spec D6 and Q4: the monthly allowance a car starts with. */
export const defaultMonthlyDistance = (unit: DistanceUnit): number => (unit === 'km' ? 1600 : 1000);

/** Spec D6: the host's daily allowance choices. */
export const dailyDistanceChoices = (unit: DistanceUnit): number[] =>
  unit === 'km' ? [150, 250, 300, 400, 500] : [100, 150, 200, 250, 300];

/** Spec 028 D7: litres per 100 km from US miles per gallon (display only; MPG stays stored). */
export const mpgToLitresPer100Km = (mpg: number): number =>
  mpg > 0 ? Math.round((235.214583 / mpg) * 10) / 10 : 0;

/**
 * "28 MPG" (today's text) on a miles brand, "8.4 L/100 km" on a metric one.
 * Empty for no value.
 */
export const formatFuelEconomy = (mpg: number | null | undefined, unit: DistanceUnit): string => {
  if (mpg == null || !(Number(mpg) > 0)) return '';
  return unit === 'km' ? `${mpgToLitresPer100Km(Number(mpg))} L/100 km` : `${mpg} MPG`;
};

/** An odometer reading in its own unit: "12,000 miles" or "19,312 km" (D11). */
export const odometerText = (value: number, unit: unknown): string =>
  `${Number(value).toLocaleString()} ${asDistanceUnit(unit) === 'km' ? 'km' : 'miles'}`;
